import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { useLocation } from "@swa-ui/browser";
import { DateSelector, Dialog, DropDown, useDialog } from "@swa-ui/core";
import { swaDate } from "@swa-ui/date";
import { CheckboxFormField, Form, TextFormField } from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { usePersistedState } from "@swa-ui/persistence";

import { MktgMediatorContext } from "../MktgMediatorProvider";
import styles from "./PlacementsUpdater.module.scss";
import { timeOptions } from "./timeOptions";

/**
 * PlacementsUpdater submits custom form field data to the Content Service Toolbox network request.
 */
export const PERSISTED_PLACEMENT_TOOLBOX_DATA_STORAGE_KEY =
  "PlacementsUpdater-persistedPlacementData";
const DROPDOWN_ROWS = [0, 1, 2, 3, 4, 5, 6, 7];
const MAX_DROPDOWN_DISPLAY = 15;

export const PlacementsUpdater = (props) => {
  const { displayToolbox } = props;
  const { dialogRef, hideDialog, isDialogVisible, showDialog } = useDialog("center");
  const { pathname } = useLocation();
  const { startUpdateCycle } = useContext(MktgMediatorContext);
  const [persistedFormData = {}, setPersistedFormData] = usePersistedState({
    key: PERSISTED_PLACEMENT_TOOLBOX_DATA_STORAGE_KEY,
  });
  const [formData, setFormData] = useState(() => getInitialFormData());
  const dropdownFields = getDropdownFields();
  const updateContentData = getBootstrapData("update-content-data");
  const updateContentDataWithSelect = ["Select"].concat(updateContentData);

  useEffect(() => {
    if (displayToolbox) {
      showDialog();
    }
  }, [displayToolbox]);

  return (
    <Form onSubmit={updatePlacements}>
      <Dialog {...getDialogProps()}>
        <div className={styles.content}>
          {renderDropDownRows()}
          {renderDateAndTimeRows()}
          {renderPersistDataCheckbox()}
        </div>
      </Dialog>
    </Form>
  );

  function renderDropDownRows() {
    return DROPDOWN_ROWS.map((rowIndex) => (
      <div key={`row${rowIndex}`} className={styles.dropDownRow}>
        <div>
          <DropDown {...getDropDownProps(rowIndex)} />
        </div>
        <div>:</div>
        <div>
          <TextFormField {...getFormFieldProps(rowIndex)} />
        </div>
      </div>
    ));
  }

  function renderDateAndTimeRows() {
    return (
      <>
        <div>
          Select date:
          <DateSelector {...getDateSelectorProps()} />
        </div>
        <div>
          Select time:
          <DropDown {...getSelectTimeProps()} />
        </div>
      </>
    );
  }

  function renderPersistDataCheckbox() {
    return (
      <div>
        <CheckboxFormField {...getPersistCheckboxProps()} />
      </div>
    );
  }

  function getDialogProps() {
    return {
      buttonGroupProps: {
        "aria-label-next": i18n("PlacementsUpdater__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("PlacementsUpdater__BUTTON_GROUP_ARIA_PREVIOUS"),
        buttonList: [
          {
            label: "Clear data",
            props: {
              name: "clearData",
              onClick: () => clearPersistedFormData(),
              styleType: "secondary",
            },
          },
          {
            label: "Submit data",
            props: {
              name: "submitData",
              styleType: "primary",
              type: "submit",
            },
          },
        ],
      },
      dialogRef,
      heading: "Content Service Toolbox",
      onClose: handleDialogClose,
      portal: false,
      revealed: isDialogVisible,
      showClose: true,
      width: "narrow",
    };
  }

  function getDropDownProps(dropdownIndex) {
    const name = `row${dropdownIndex}Property`;

    return {
      defaultValue: "Select",
      id: name,
      list: updateContentDataWithSelect,
      maxItemsToDisplay: MAX_DROPDOWN_DISPLAY,
      name,
      onChange: (event) => handleFormDataChange(name, event.target.value),
      value: dropdownFields[dropdownIndex][0],
    };
  }

  function getFormFieldProps(formFieldIndex) {
    const name = `row${formFieldIndex}Value`;

    return {
      componentProps: {
        onChange: (event) => handleFormDataChange(name, event.target.value),
        value: dropdownFields[formFieldIndex][1],
      },
      hideBottomSection: true,
      name,
      type: "text",
    };
  }

  function getDateSelectorProps() {
    return {
      closeContent: "done",
      dateStart: formData.selectedDate,
      daysOfWeekInitials: "SMTWTFS",
      location: "above",
      name: "selectedDate",
      numberCalendars: 1,
      onSelectStart: (date) => handleFormDataChange("selectedDate", date),
      singleDate: true,
      title: "Select date",
    };
  }

  function getSelectTimeProps() {
    return {
      defaultValue: "12:00 PM",
      id: "mockId",
      list: timeOptions,
      maxItemsToDisplay: MAX_DROPDOWN_DISPLAY,
      name: "dropdown",
      onChange: (event) =>
        handleFormDataChange("curDate", swaDate(event.target.value, "h:mm a").valueOf()),
      value: getTimeDropDownValue(persistedFormData?.curDate),
    };
  }

  function getPersistCheckboxProps() {
    return {
      componentProps: {
        label: "Save values",
        onChange: () =>
          handleFormDataChange("persistPlacementData", !formData.persistPlacementData),
        value: formData.persistPlacementData ?? false,
      },
      name: "persistPlacementData",
    };
  }

  function getInitialFormData() {
    return {
      ...persistedFormData,
      curDate: getTimeValue(persistedFormData.curDate),
      selectedDate: getSelectedDate(persistedFormData.selectedDate),
    };
  }

  function getDropdownFields() {
    const rowsFormData = Object.fromEntries(
      Object.entries(formData).filter(
        ([itemName]) =>
          itemName !== "curDate" &&
          itemName !== "selectedDate" &&
          itemName !== "persistPlacementData"
      )
    );

    return DROPDOWN_ROWS.map((index) => {
      const rowProperty = rowsFormData[`row${index}Property`];
      const rowValue = rowsFormData[`row${index}Value`];

      return [rowProperty ?? "Select", rowValue ?? ""];
    });
  }

  function getSelectedDate(date) {
    const startDate = swaDate(date);
    const todaysDate = swaDate();

    return (!startDate || todaysDate.isAfter(startDate) ? todaysDate : startDate).format(
      "MM/DD/YYYY"
    );
  }

  function getSelectedDateAndTime() {
    const { curDate, selectedDate } = formData;

    return swaDate(`${selectedDate} ${curDate}`, "MM/DD/YYYY h:mm a");
  }

  function getTimeValue(time) {
    let date = swaDate(time);

    if (!time) {
      date = date.startOf("hour");
    }

    return date;
  }

  function getTimeDropDownValue(time) {
    return getTimeValue(time).format("h:mm a");
  }

  function getFormFieldData() {
    const selectedDateAndTime = getSelectedDateAndTime();
    const formattedDate = selectedDateAndTime.format("MM/DD/YYYY hh:mm:ss");
    const curDate = selectedDateAndTime.valueOf();
    const dropdownAndFormFields = {};

    DROPDOWN_ROWS.forEach((rowIndex) => {
      const [dropdownLabel, formFieldInput] = dropdownFields[rowIndex];

      if (dropdownLabel && dropdownLabel !== "Select" && formFieldInput) {
        dropdownAndFormFields[dropdownLabel] = formFieldInput;
      }
    });

    return {
      ...dropdownAndFormFields,
      curDate,
      persistPlacementData: formData.persistPlacementData,
      selectedDate: formattedDate,
    };
  }

  function handleDialogClose() {
    hideDialog();
  }

  function handleFormDataChange(name, value) {
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  function clearPersistedFormData() {
    setFormData({});
    setPersistedFormData({});
  }

  function updatePlacements() {
    const { persistPlacementData, ...formFieldData } = getFormFieldData();

    if (persistPlacementData) {
      setPersistedFormData(formData);
    }

    hideDialog();
    startUpdateCycle(pathname, formFieldData, true);
  }
};

PlacementsUpdater.displayName = "PlacementsUpdater";
PlacementsUpdater.propTypes = {
  /**
   * State that indicates whether toolbox is currently displayed or not.
   */
  displayToolbox: PropTypes.bool,
};
